import React, { useState, useEffect } from 'react';
import { Settings, Clock, User, Building, Calendar, Star, UserCheck, Bell, Palette, AlertTriangle, Database, CreditCard, Bot, UserPlus, Crown, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TaskTypeDefaultsManager from '../../components/scheduling/TaskTypeDefaultsManager';
import CalendarPermissionsManager from '../../components/CalendarPermissionsManager';
import LockedTimeManager from '../../components/LockedTimeManager';
import WorkdaySettingsManager from '../../components/settings/WorkdaySettingsManager';
import FirmWorkdayDefaultsManager from '../../components/settings/FirmWorkdayDefaultsManager';
import SuperAdminFirmManager from '../../components/settings/SuperAdminFirmManager';
import SystemAlertManager from '../../components/settings/SystemAlertManager';
import TemplateLibrary from '../../components/TemplateLibrary';
import CommunicationPreferences from '../../components/settings/CommunicationPreferences';
import BrandingSettings from '../../components/settings/BrandingSettings';
import LawPaySettings from '../../components/settings/LawPaySettings';
import AISettings from '../../components/settings/AISettings';
import BYOKSettings from '../../components/settings/BYOKSettings';
import IntakeStatusManager from '../../components/settings/IntakeStatusManager';
import SubscriptionSettings from '../../components/settings/SubscriptionSettings';
import ProfileSettings from '../../components/settings/ProfileSettings';
import firmService from '../../services/firmService';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    checkSuperAdminStatus();
  }, []);

  const checkSuperAdminStatus = async () => {
    try {
      const result = await firmService.checkSuperAdmin();
      setIsSuperAdmin(result.is_super_admin);
    } catch (err) {
      console.error('Error checking super admin status:', err);
      setIsSuperAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  // Support deep-linking to tabs via ?tab=... (wait for admin check to finish)
  useEffect(() => {
    if (checkingAdmin) return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [checkingAdmin]);

  const tabSections = [
    {
      label: 'Account',
      tabs: [
        { id: 'profile', name: 'Profile', icon: User, description: 'Profile picture and personal info' },
        { id: 'subscription', name: 'Subscription', icon: Crown, description: 'Plan, billing, and usage' },
      ],
    },
    {
      label: 'Personal',
      tabs: [
        { id: 'personal', name: 'Working Hours', icon: Clock, description: 'Your schedule and preferences' },
        { id: 'communication-preferences', name: 'Notifications', icon: Bell, description: 'Communication filters and alerts' },
      ],
    },
    {
      label: 'Firm',
      tabs: [
        { id: 'firm', name: 'Firm Defaults', icon: Building, description: 'Firm-wide working hours and config' },
        { id: 'branding', name: 'Branding', icon: Palette, description: 'Logo, colors, and firm identity' },
        { id: 'team', name: 'Team Management', icon: UserCheck, description: 'Users, roles, and permissions', isLink: true, path: '/users' },
        { id: 'intake-statuses', name: 'Intake Statuses', icon: UserPlus, description: 'Define intake pipeline stages' },
      ],
    },
    {
      label: 'Workflow',
      tabs: [
        { id: 'task-defaults', name: 'Task Defaults', icon: Clock, description: 'Default time estimates for legal work' },
        { id: 'templates', name: 'Templates', icon: Star, description: 'Case templates and workflows' },
        { id: 'scheduling', name: 'Scheduling', icon: Calendar, description: 'Calendar permissions and blocked time' },
      ],
    },
    {
      label: 'Integrations',
      tabs: [
        { id: 'payments', name: 'Payments', icon: CreditCard, description: 'LawPay integration settings' },
        { id: 'ai-provider', name: 'AI Provider', icon: Key, description: 'Bring your own AI API key' },
      ],
    },
    ...(isSuperAdmin ? [{
      label: 'Administration',
      tabs: [
        { id: 'firms', name: 'Firms', icon: Building, description: 'Manage firms and configurations', isLink: true, path: '/firms' },
        { id: 'migrations', name: 'Migrations', icon: Database, description: 'Manage data migration jobs', isLink: true, path: '/migrations' },
        { id: 'ai-settings', name: 'AI Settings', icon: Bot, description: 'AI model and token limits per firm' },
        { id: 'system-alerts', name: 'System Alerts', icon: AlertTriangle, description: 'System-wide alert banners' },
      ],
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center">
              <Settings className="h-6 w-6 text-gray-500 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            </div>
            <p className="mt-2 text-gray-600">
              Manage your preferences, firm settings, and system configurations
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Settings Navigation */}
          <div className="lg:w-64">
            <nav className="space-y-4">
              {tabSections.map((section) => (
                <div key={section.label}>
                  <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                    {section.label}
                  </h3>
                  <div className="space-y-0.5">
                    {section.tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => tab.isLink ? navigate(tab.path) : setActiveTab(tab.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === tab.id && !tab.isLink
                              ? 'bg-gray-100 text-yellow-600 border-l-4 border-yellow-500'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center">
                            <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
                            <span>{tab.name}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Settings Content */}
          <div className="flex-1">
            {activeTab === 'profile' && (
              <div>
                <ProfileSettings />
              </div>
            )}

            {activeTab === 'subscription' && (
              <div>
                <SubscriptionSettings />
              </div>
            )}

            {activeTab === 'task-defaults' && (
              <div>
                <TaskTypeDefaultsManager />
              </div>
            )}

            {activeTab === 'scheduling' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Calendar Permissions</h3>
                  <CalendarPermissionsManager />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Blocked Time</h3>
                  <LockedTimeManager />
                </div>
              </div>
            )}

            {activeTab === 'templates' && (
              <div>
                <TemplateLibrary />
              </div>
            )}

            {activeTab === 'branding' && (
              <div>
                <BrandingSettings />
              </div>
            )}

            {activeTab === 'payments' && (
              <div>
                <LawPaySettings />
              </div>
            )}

            {activeTab === 'ai-provider' && (
              <div>
                <BYOKSettings />
              </div>
            )}

            {activeTab === 'communication-preferences' && (
              <div className="bg-white rounded-lg shadow">
                <CommunicationPreferences />
              </div>
            )}

            {activeTab === 'personal' && (
              <div>
                <WorkdaySettingsManager />
              </div>
            )}

            {activeTab === 'firm' && (
              <div>
                {checkingAdmin ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    <span className="ml-3 text-gray-600">Loading...</span>
                  </div>
                ) : isSuperAdmin ? (
                  <SuperAdminFirmManager />
                ) : (
                  <FirmWorkdayDefaultsManager />
                )}
              </div>
            )}

            {activeTab === 'intake-statuses' && (
              <div>
                <IntakeStatusManager />
              </div>
            )}

            {activeTab === 'ai-settings' && isSuperAdmin && (
              <div>
                <AISettings />
              </div>
            )}

            {activeTab === 'system-alerts' && (
              checkingAdmin ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                  <span className="ml-3 text-gray-600">Loading...</span>
                </div>
              ) : isSuperAdmin ? (
                <div>
                  <SystemAlertManager />
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
